import { useLayoutEffect, useRef } from "react";
import { usePage } from "@inertiajs/react";
import { analytics } from "@lib/analytics";
import type { SharedPageProps } from "~/layouts/types";

/**
 * Identifies or resets the current user in PostHog analytics.
 *
 * Only touches PostHog on real identity transitions:
 *   undefined → <id>  : first load while signed in → identify
 *   <id>      → <id'> : user switch → identify
 *   <id>      → null  : signed out → reset
 *   undefined → null  : first load while anonymous → DO NOTHING
 *   null      → null  : subsequent anonymous nav   → DO NOTHING
 *
 * The "do nothing on anonymous first load" case is load-bearing for
 * cross-subdomain stitching: launch10.com and app.launch10.com share the
 * PostHog cookie via `cross_subdomain_cookie: true`, so a visitor arriving
 * from the marketing site already has a distinct_id. Calling reset() here
 * would clobber it and orphan every pre-signup event.
 */
export function usePosthogIdentify() {
  const props = usePage().props as SharedPageProps;
  const lastUserIdRef = useRef<number | null | undefined>(undefined);

  const currentUserId = props.current_user?.id ?? null;
  const currentEmail = props.current_user?.email;
  const currentName = props.current_user?.name;

  useLayoutEffect(() => {
    const previousId = lastUserIdRef.current;
    if (currentUserId === previousId) return;
    lastUserIdRef.current = currentUserId;

    if (currentUserId != null) {
      analytics.identify(currentUserId, { email: currentEmail, name: currentName });
    } else if (previousId != null) {
      // Was signed in, now signed out — real logout.
      analytics.reset();
    }
    // else: first load while anonymous. Preserve any cross-subdomain distinct_id.
  }, [currentUserId, currentEmail, currentName]);
}
